/* Stix PDP Stock-Aware Picker — shared helpers
 * ---------------------------------------------
 * Used by stix-picker-A.js / D.js / E.js. Provides:
 *   - StixPicker.catalog: parsed live variant catalog from the router DOM blob
 *   - StixPicker.oosRecs: parsed oos_recs metafield blob
 *   - StixPicker.findVariant({optionName: value, ...}) → variant or null
 *   - StixPicker.variantStatus(sel) → 'in-stock' | 'oos' | 'not-offered'
 *   - StixPicker.fireVariantClick(variant, arm) → drives the same tracking
 *     events the live picker fires (stix.variant_selector, stix.out_of_stock,
 *     stix.track.trackVariantClick, variant:change DOM event)
 *   - StixPicker.applyVariant(variant) → programmatically clicks the
 *     underlying <input type="radio" name="options[...]"> inputs so the
 *     existing productForm.onOptionChange handler fires unchanged.
 *   - StixPicker.attachOosHero(rootEl, arm) → returns {update(sel, status)}
 *     for cross-sell + BIS waitlist (mirrors snippets/oos-substitute.liquid)
 *
 * Loaded after stix-picker-test-router.js, before the per-arm scripts.
 */
(function () {
  'use strict';

  var routerEl = document.querySelector('[data-cl-picker-router]');
  if (!routerEl) return;

  var recsNode = routerEl.querySelector('[data-cl-oos-recs]');
  var oosRecs = {};
  try { oosRecs = recsNode ? JSON.parse(recsNode.textContent) : {}; } catch (e) {}

  /* Build the merged 5-dim variant catalog from window.stix_api.product.json,
     which the theme's snippets/unlimited-options.liquid populates by merging
     all linked-family product variants into a single 5-option catalog. This
     is the SAME data the live productForm reads, so our pickers see exactly
     what shoppers can actually buy across the product family. */
  function buildCatalog() {
    var j = (window.stix_api && window.stix_api.product && window.stix_api.product.json) || null;
    if (!j || !j.options || !j.variants) return null;
    /* unlimited-options.liquid sometimes wraps option.values in an extra
       array for expansion options (e.g. values: [["Graphite","Steel"]]).
       Flatten so we always have a plain list of strings. */
    var optionNames = j.options.map(function (o) { return o.name; });
    var variants = j.variants.map(function (v) {
      return {
        id: v.id,
        sku: v.sku || '',
        title: v.title || '',
        available: !!v.available,
        price: (typeof v.price === 'number') ? (v.price / 100) : (parseFloat(v.price) || 0),
        url: v.url || '',
        options: (v.options || []).slice(0, optionNames.length),
        product_id: v.product_id || null
      };
    });
    return {
      productHandle: j.handle || '',
      productTitle: j.title || '',
      productImage: (j.featured_image && j.featured_image.src) || (j.images && j.images[0]) || '',
      optionNames: optionNames,
      variants: variants
    };
  }
  var catalog = buildCatalog();
  if (!catalog) {
    /* stix_api.product.json not yet populated. Poll until it is. */
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      catalog = buildCatalog();
      if (catalog || tries > 40) {
        clearInterval(poll);
        if (catalog) {
          window.StixPicker = window.StixPicker || {};
          window.StixPicker.catalog = catalog;
          document.dispatchEvent(new CustomEvent('cl-picker-catalog-ready', { detail: { catalog: catalog } }));
        }
      }
    }, 50);
    return;
  }

  /* --- Variant lookup helpers ---------------------------------------------- */
  function findVariantByOptionValues(optionValues) {
    /* optionValues = array of strings in product.options_with_values order */
    return catalog.variants.find(function (v) {
      for (var i = 0; i < optionValues.length; i++) {
        if (v.options[i] !== optionValues[i]) return false;
      }
      return true;
    }) || null;
  }
  function findVariantBySel(sel) {
    /* sel = {OptionName: value, ...} */
    var optionValues = catalog.optionNames.map(function (name) { return sel[name]; });
    return findVariantByOptionValues(optionValues);
  }
  function variantStatus(sel) {
    var v = findVariantBySel(sel);
    if (!v) return 'not-offered';
    if (!v.available) return 'oos';
    return 'in-stock';
  }
  function valueOffered(optionName, value) {
    return catalog.variants.some(function (v) {
      var idx = catalog.optionNames.indexOf(optionName);
      return idx >= 0 && v.options[idx] === value;
    });
  }
  function probeStatus(sel, optionName, value) {
    var probe = {};
    Object.keys(sel).forEach(function (k) { probe[k] = sel[k]; });
    probe[optionName] = value;
    return variantStatus(probe);
  }

  /* --- Closest in-stock (Hamming-weighted, flex-step bias) ----------------- */
  var FLEX_ORDER = { Active: 0, Regular: 1, Stiff: 2, Senior: 0, 'Extra Stiff': 3, Ladies: -1 };
  function closestInStock(sel, dimWeights) {
    /* PIN Hand and Length — never auto-swap. A Left-handed shopper must not
       see a Right-handed recommendation. A 6'4 shopper must not see a Standard
       length. We hard-filter on those dimensions. */
    var pinDims = [];
    catalog.optionNames.forEach(function (n) {
      if (/hand|dex|length|size/i.test(n) && sel[n]) pinDims.push(n);
    });
    var W = dimWeights || {
      'Shaft Length': 1000, 'Length': 1000,
      'Hand': 500, 'Dexterity': 500, 'Handedness': 500,
      'Shaft Flex': 100, 'Flex': 100,
      'Include Bag': 50, 'Bag': 50,
      'Shaft Material': 10, 'Material': 10
    };
    var userFlexRank = null;
    catalog.optionNames.forEach(function (name) {
      if (/flex/i.test(name) && sel[name] in FLEX_ORDER) userFlexRank = FLEX_ORDER[sel[name]];
    });
    var best = null, bestScore = -Infinity;
    catalog.variants.forEach(function (v) {
      if (!v.available) return;
      /* Hard pin filter */
      for (var pi = 0; pi < pinDims.length; pi++) {
        var idx = catalog.optionNames.indexOf(pinDims[pi]);
        if (idx >= 0 && v.options[idx] !== sel[pinDims[pi]]) return;
      }
      var score = 0;
      catalog.optionNames.forEach(function (name, i) {
        if (sel[name] && v.options[i] === sel[name]) {
          score += (W[name] || 1);
        }
      });
      if (userFlexRank !== null) {
        catalog.optionNames.forEach(function (name, i) {
          if (/flex/i.test(name) && v.options[i] in FLEX_ORDER) {
            score -= Math.abs(FLEX_ORDER[v.options[i]] - userFlexRank) * 20;
          }
        });
      }
      if (score > bestScore) { bestScore = score; best = v; }
    });
    if (!best) return null;
    var bestSel = {};
    catalog.optionNames.forEach(function (name, i) { bestSel[name] = best.options[i]; });
    return { variant: best, sel: bestSel };
  }

  /* --- Drive the underlying radio inputs (control picker) ------------------ */
  /* This is the integration seam. By clicking the live radio inputs, we let
     the existing productForm.onOptionChange fire all the tracking + UI
     updates that the control arm fires. No duplication, no divergence. */
  function applyVariant(sel) {
    /* Drive the live control picker's radios (which include the
       unlimited-options-buttons radios for Material/Bag). productForm
       listens to change events on these and updates the form's hidden
       input[name=id] + the live ATC button text natively. The control
       picker may be hidden via display:none for treatment arms, but its
       <input> elements remain in the DOM and stay accessible to JS. */
    var control = document.querySelector('[data-cl-control-picker]');
    if (!control) return;
    catalog.optionNames.forEach(function (name) {
      var val = sel[name];
      if (!val) return;
      var radios = control.querySelectorAll('input[type="radio"][name="options[' + name + ']"]');
      radios.forEach(function (input) {
        if (input.value === val && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* --- Tracking — fires the same events the live picker fires -------------- */
  function fireVariantEvents(sel, arm) {
    var variant = findVariantBySel(sel);
    var status = variantStatus(sel);
    var inStock = (status === 'in-stock');

    /* 1. stix.variant_selector (dataLayer) — same as live onOptionChange */
    try {
      (window.dataLayer = window.dataLayer || []).push({
        event: 'stix.variant_selector',
        variant_id: variant ? variant.id : null,
        variant_available: !!(variant && variant.available),
        cl_test_id: 'pdp_stockaware_picker',
        cl_test_variant: arm
      });
    } catch (e) {}

    /* 2. stix.track.trackVariantClick — the Klaviyo + TW fan-out */
    try {
      if (window.stix && window.stix.track && window.stix.track.trackVariantClick && variant) {
        window.stix.track.trackVariantClick({
          handle: catalog.productHandle,
          sku: variant.sku || '',
          variant_id: variant.id,
          options: catalog.optionNames.map(function (n, i) {
            return { name: n, value: variant.options[i] };
          }),
          in_stock: inStock,
          price: variant.price ? variant.price * 100 : null,
          cl_test_id: 'pdp_stockaware_picker',
          cl_test_variant: arm
        });
      }
    } catch (e) {}

    /* 3. stix.out_of_stock (dataLayer) — when OOS */
    if (status === 'oos') {
      try {
        (window.dataLayer = window.dataLayer || []).push({
          event: 'stix.out_of_stock',
          variant_id: variant ? variant.id : null,
          sku: variant ? variant.sku : null,
          variant_options: variant ? variant.title : null,
          product_title: catalog.productTitle,
          cl_test_id: 'pdp_stockaware_picker',
          cl_test_variant: arm
        });
      } catch (e) {}
    }

    /* 4. DOM variant:change — consumed by oos-substitute.liquid */
    try {
      var detail = { variant: variant, status: status, sel: sel, arm: arm };
      document.dispatchEvent(new CustomEvent('variant:change', { detail: detail, bubbles: true }));
    } catch (e) {}
  }

  /* --- OOS hero (cross-sell + BIS waitlist) -------------------------------- */
  /* Mirrors snippets/oos-substitute.liquid card. Mounts inside whatever
     element the caller passes as the mount. */
  function attachOosHero(mountEl, arm) {
    if (!mountEl) return { update: function () {} };

    mountEl.innerHTML = ''
      + '<div class="cl-oos-hero" hidden>'
      +   '<header class="cl-oos-hero__header">'
      +     '<span class="cl-oos-hero__dot" aria-hidden="true"></span>'
      +     '<p class="cl-oos-hero__lead"><strong>Your best fit right now</strong><br><span class="cl-oos-hero__meta">based on <span data-cl-oos-current>your pick</span></span></p>'
      +   '</header>'
      +   '<article class="cl-oos-hero__card">'
      +     '<div class="cl-oos-hero__body">'
      +       '<div class="cl-oos-hero__img" data-cl-oos-img></div>'
      +       '<div class="cl-oos-hero__content">'
      +         '<p class="cl-oos-hero__title" data-cl-oos-title></p>'
      +         '<p class="cl-oos-hero__meta"><span data-cl-oos-price></span><span class="cl-oos-hero__inv" data-cl-oos-inv>In stock</span></p>'
      +         '<p class="cl-oos-hero__fit" data-cl-oos-fit></p>'
      +       '</div>'
      +     '</div>'
      +     '<div class="cl-oos-hero__cta-row">'
      +       '<button type="button" class="cl-oos-hero__cta" data-cl-oos-cta></button>'
      +     '</div>'
      +     '<div class="cl-oos-hero__alts" data-cl-oos-alts></div>'
      +     '<div class="cl-oos-hero__bis">'
      +       '<button type="button" class="cl-oos-hero__bis-toggle" data-cl-bis-toggle><span data-cl-bis-toggle-text></span></button>'
      +       '<div class="cl-oos-hero__bis-form" data-cl-bis-form>'
      +         '<input type="email" placeholder="you@example.com" data-cl-bis-email>'
      +         '<button type="button" data-cl-bis-submit>Notify Me</button>'
      +       '</div>'
      +       '<div class="cl-oos-hero__bis-success" data-cl-bis-success>&check; We will email you when it is back.</div>'
      +     '</div>'
      +   '</article>'
      + '</div>';

    var hero = mountEl.querySelector('.cl-oos-hero');
    var $cur = mountEl.querySelector('[data-cl-oos-current]');
    var $img = mountEl.querySelector('[data-cl-oos-img]');
    var $title = mountEl.querySelector('[data-cl-oos-title]');
    var $price = mountEl.querySelector('[data-cl-oos-price]');
    var $inv = mountEl.querySelector('[data-cl-oos-inv]');
    var $fit = mountEl.querySelector('[data-cl-oos-fit]');
    var $cta = mountEl.querySelector('[data-cl-oos-cta]');
    var $alts = mountEl.querySelector('[data-cl-oos-alts]');
    var $bisToggleText = mountEl.querySelector('[data-cl-bis-toggle-text]');
    var $bisForm = mountEl.querySelector('[data-cl-bis-form]');
    var $bisEmail = mountEl.querySelector('[data-cl-bis-email]');
    var $bisSuccess = mountEl.querySelector('[data-cl-bis-success]');

    var lastSel = null;
    /* BIS form is always inline now — no toggle needed. */
    var $bisSubmit = mountEl.querySelector('[data-cl-bis-submit]');
    function doBisSubmit() {
      var email = $bisEmail.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        $bisEmail.focus();
        return;
      }
      var variant = findVariantBySel(lastSel || {});
      submitBis(variant ? variant.id : null, email, lastSel, arm, function () {
        $bisForm.style.display = 'none';
        $bisSuccess.classList.add('is-open');
      });
    }
    if ($bisSubmit) $bisSubmit.addEventListener('click', doBisSubmit);
    if ($bisEmail) $bisEmail.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); doBisSubmit(); }
    });

    function update(sel, status) {
      lastSel = Object.assign({}, sel);
      $bisSuccess.classList.remove('is-open');
      $bisForm.classList.remove('is-open');
      if (status === 'in-stock') { hero.setAttribute('hidden', ''); return; }
      hero.removeAttribute('hidden');

      $cur.textContent = variantLabel(sel);
      $bisToggleText.textContent = oosRecs.waitlistCopy || 'Email me when this combo is back';

      var near = nearestSameProduct(sel);
      $alts.innerHTML = '';

      if (near) {
        $img.innerHTML = catalog.productImage ? '<img src="' + catalog.productImage + '" alt="">' : '';
        $title.textContent = catalog.productTitle + ' · ' + (near.flexLabel || near.label);
        $price.textContent = formatMoney(near.variant.price);
        $inv.textContent = 'In stock — ships in 1-2 days';
        $fit.textContent = near.copy;
        $cta.textContent = 'Switch to ' + (near.flexLabel || near.label) + '  →';
        $cta.onclick = function () {
          fireEvent('OOS Hero Click', { kind: 'nearest-swap', to_variant: near.variant.id }, arm);
          /* Dispatch oos-hero:swap so the arm picker updates its internal sel +
             re-renders chips/dropdowns. The arm's listener also calls applyVariant
             so the underlying form gets driven. */
          try {
            mountEl.dispatchEvent(new CustomEvent('oos-hero:swap', { detail: { sel: near.sel }, bubbles: true }));
          } catch (e) {
            applyVariant(near.sel); /* fallback if event dispatch fails */
          }
        };
        if (oosRecs.primarySub) {
          var pUrl = crossSellUrlFor(oosRecs.primarySub, sel);
          addAltRow($alts, 'Want the tier up?', oosRecs.primarySub.title, pUrl, arm);
        }
      } else if (oosRecs.primarySub) {
        $img.innerHTML = oosRecs.primarySub.imageUrl ? '<img src="' + oosRecs.primarySub.imageUrl + '" alt="">' : '';
        $title.textContent = oosRecs.primarySub.title;
        $price.textContent = oosRecs.primarySub.price || '';
        $inv.textContent = 'In stock';
        $fit.textContent = oosRecs.primarySub.reason || oosRecs.fitCopy || '';
        $cta.textContent = 'Shop Now';
        $cta.onclick = function () {
          var crossUrl = crossSellUrlFor(oosRecs.primarySub, sel);
          fireEvent('Cross-Sell Click', { kind: 'hero-fallback', product: oosRecs.primarySub.title, url: crossUrl }, arm);
          window.location.href = crossUrl;
        };
        if (oosRecs.secondarySub) {
          var secUrl = crossSellUrlFor(oosRecs.secondarySub, sel);
          addAltRow($alts, 'Also a strong match', oosRecs.secondarySub.title, secUrl, arm);
        }
      } else {
        hero.setAttribute('hidden', '');
        return;
      }

      fireEvent('OOS Form Viewed', {
        sku: skuFor(sel),
        variant_options: variantLabel(sel)
      }, arm);
    }

    return { update: update };
  }

  /* --- Helpers ------------------------------------------------------------- */
  function variantLabel(sel) {
    return catalog.optionNames.map(function (n) { return sel[n] || '?'; }).join(' / ');
  }
  function skuFor(sel) {
    var v = findVariantBySel(sel);
    return v ? v.sku : '';
  }
  function formatMoney(amt) {
    if (amt == null) return '';
    return '$' + (Math.round(amt * 100) / 100).toFixed(2).replace(/\.00$/, '');
  }
  function nearestSameProduct(sel) {
    var flexOptName = catalog.optionNames.find(function (n) { return /flex/i.test(n); });
    var lockedDFL = catalog.optionNames.map(function (n) {
      var val = sel[n] || '';
      return val.charAt(0).toUpperCase();
    }).join('');
    /* Try flex_swap_rules from oos_recs metafield first */
    if (flexOptName && oosRecs.flexRules) {
      /* Try a short DFL key (Hand+Flex+Length) — varies by product. Look up
         any matching key in flexRules. */
      var lookupKeys = [
        lockedDFL,
        (sel[catalog.optionNames.find(function(n){return /hand|dex/i.test(n);})]||'').charAt(0) +
        (sel[flexOptName]||'').charAt(0) +
        (sel[catalog.optionNames.find(function(n){return /length|size/i.test(n);})]||'').charAt(0)
      ];
      for (var i = 0; i < lookupKeys.length; i++) {
        var rule = oosRecs.flexRules[lookupKeys[i]];
        if (rule && (rule.softer || rule.stiffer)) {
          var cand = rule.softer || rule.stiffer;
          var probe = Object.assign({}, sel);
          probe[flexOptName] = cand.flex_code || cand.flex_label;
          var v = findVariantBySel(probe);
          if (v && v.available) {
            return { variant: v, sel: probe, copy: cand.copy || '', flexLabel: cand.flex_label || cand.flex_code, label: cand.flex_label };
          }
        }
      }
    }
    /* Fallback: closestInStock with flex-step bias */
    var closest = closestInStock(sel);
    if (!closest) return null;
    /* Generate copy comparing closest to user pick */
    var diffs = [];
    catalog.optionNames.forEach(function (n, i) {
      if (sel[n] && closest.variant.options[i] !== sel[n]) {
        diffs.push(n + ' = ' + closest.variant.options[i]);
      }
    });
    var copy = diffs.length === 0
      ? 'Exact match is back in stock — pick this.'
      : diffs.length === 1
        ? 'Same set, just in ' + diffs[0] + '. Ships in 1-2 days.'
        : 'Closest in-stock combo: ' + diffs.join(', ');
    return { variant: closest.variant, sel: closest.sel, copy: copy, flexLabel: '', label: 'in-stock alternative' };
  }
  function addAltRow($alts, label, title, url, arm) {
    var row = document.createElement('div');
    row.className = 'cl-oos-hero__alt-row';
    var lab = document.createElement('span');
    lab.className = 'cl-oos-hero__alt-label';
    lab.textContent = label;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cl-oos-hero__alt-link';
    btn.textContent = title + ' →';
    btn.addEventListener('click', function () {
      fireEvent('Cross-Sell Click', { product: title, url: url }, arm);
      if (url) window.location.href = url;
    });
    row.appendChild(lab);
    row.appendChild(btn);
    $alts.appendChild(row);
  }
  function submitBis(variantId, email, sel, arm, onSuccess) {
    /* Mirrors snippets/oos-form.liquid + assets/main-product.js _initBackInStock:
       same endpoint, same payload shape, same revision header, same on-success
       events. Klaviyo flows attached to 'oos-notify-me' source or
       'stix.stock_notification_email' event will trigger identically whether
       the BIS came from the live form or from this picker. We additionally
       fire 'BIS Signup Submitted' tagged with cl_test_variant for arm
       attribution in the CRO read-out. */
    if (!variantId) { if (onSuccess) onSuccess(); return; }
    var apiKey = (window.stix_api && stix_api.vendors && stix_api.vendors.klaviyoPublicAPIKey) || '';
    if (!apiKey) {
      /* No key in this page state — call onSuccess so UI doesn't hang. */
      fireEvent('BIS Signup Failed', { reason: 'no klaviyo api key', sku: skuFor(sel||{}), variant_id: variantId }, arm);
      if (onSuccess) onSuccess();
      return;
    }
    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: email } } }
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
        }
      }
    };
    fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(apiKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-06-15',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (response.status === 202) {
        /* Same identify + dataLayer event the live oos-form fires on success.
           Keeps Klaviyo profile state + downstream flows consistent across
           sources. */
        try {
          if (window.stix && window.stix.track && window.stix.track.identify) {
            window.stix.track.identify(email, { 'Email Capture Source': 'oos-notify-me' });
          }
        } catch (e) {}
        try {
          (window.dataLayer = window.dataLayer || []).push({
            event: 'stix.stock_notification_email',
            variant_id: variantId
          });
        } catch (e) {}
        /* Test-attribution event — adds cl_test_id / cl_test_variant + sku */
        fireEvent('BIS Signup Submitted', {
          variant_id: variantId,
          sku: skuFor(sel||{}),
          variant_options: variantLabel(sel||{}),
          product_handle: (window.stix_api && stix_api.product && stix_api.product.json && stix_api.product.json.handle) || ''
        }, arm);
      } else {
        fireEvent('BIS Signup Failed', { status: response.status, variant_id: variantId }, arm);
      }
      if (onSuccess) onSuccess();
    }).catch(function (err) {
      fireEvent('BIS Signup Error', { error: String(err), variant_id: variantId }, arm);
      if (onSuccess) onSuccess();
    });
  }
  function fireEvent(name, props, arm) {
    var merged = Object.assign({
      cl_test_id: 'pdp_stockaware_picker',
      cl_test_variant: arm
    }, props || {});
    try { if (window.gtag) window.gtag('event', name, merged); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, merged)); } catch (e) {}
    try {
      if (window.stix && window.stix.track && window.stix.track.trackEvent) {
        window.stix.track.trackEvent(name, merged);
      }
    } catch (e) {}
  }

  /* --- Initial selection: from ?variant URL param, else first available ---- */
  function initialSel() {
    var url = new URL(window.location.href);
    var vid = parseInt(url.searchParams.get('variant') || '0', 10);
    var match = vid && catalog.variants.find(function (v) { return v.id === vid; });
    if (!match) {
      match = catalog.variants.find(function (v) { return v.available; }) || catalog.variants[0];
    }
    var sel = {};
    if (match) {
      catalog.optionNames.forEach(function (name, i) { sel[name] = match.options[i]; });
    }
    return sel;
  }

  /* --- Quick Pick — flat select listing every in-stock variant ------------- */
  function attachQuickPick(mountEl, arm, onPick) {
    if (!mountEl) return;
    var instock = catalog.variants.filter(function (v) { return v.available; });
    mountEl.innerHTML = ''
      + '<div class="cl-quick-pick">'
      +   '<label class="cl-quick-pick__label">Know what you want? <span>Quick pick →</span></label>'
      +   '<select class="cl-quick-pick__select" data-cl-quick-pick>'
      +     '<option value="">All ' + instock.length + ' in-stock combos…</option>'
      +     instock.map(function (v) {
                var price = '$' + (Math.round(v.price * 100) / 100).toFixed(2).replace(/\.00$/, '');
                return '<option value="' + v.id + '">' + v.title + '  ·  ' + price + '</option>';
              }).join('')
      +   '</select>'
      + '</div>';
    var $sel = mountEl.querySelector('[data-cl-quick-pick]');
    if (!$sel) return;
    $sel.addEventListener('change', function () {
      var vid = parseInt($sel.value, 10);
      if (!vid) return;
      var v = catalog.variants.find(function (x) { return x.id === vid; });
      if (!v) return;
      var sel = {};
      catalog.optionNames.forEach(function (name, i) { sel[name] = v.options[i]; });
      fireEvent('Quick Pick Used', { variant_id: vid, sku: v.sku, variant_options: v.title }, arm);
      if (typeof onPick === 'function') onPick(sel);
    });
  }

  /* --- Custom CSS Dropdown (not native <select>) --------------------------- */
  /* Returns an object with .setOptions(list, currentValue) and .getValue().
     `list` is an array of {value, label, sub, status} where status is
     'in-stock' | 'oos' | 'not-offered'. onChange fires when user picks. */
  function makeDropdown(mountEl, opts) {
    opts = opts || {};
    var onChange = opts.onChange || function () {};
    mountEl.innerHTML = ''
      + '<button type="button" class="cl-css-select" data-cl-select-trigger aria-haspopup="listbox" aria-expanded="false">'
      +   '<span class="cl-css-select__value" data-cl-select-value></span>'
      +   '<span class="cl-css-select__chevron" aria-hidden="true"></span>'
      + '</button>'
      + '<ul class="cl-css-select__panel" role="listbox" data-cl-select-panel hidden></ul>';

    var $trigger = mountEl.querySelector('[data-cl-select-trigger]');
    var $value = mountEl.querySelector('[data-cl-select-value]');
    var $panel = mountEl.querySelector('[data-cl-select-panel]');
    var currentValue = null;
    var currentList = [];
    var isOpen = false;

    function open() {
      isOpen = true;
      $panel.hidden = false;
      $trigger.setAttribute('aria-expanded', 'true');
      $trigger.classList.add('is-open');
      document.addEventListener('click', onDocClick, true);
      document.addEventListener('keydown', onKeydown, true);
    }
    function close() {
      isOpen = false;
      $panel.hidden = true;
      $trigger.setAttribute('aria-expanded', 'false');
      $trigger.classList.remove('is-open');
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('keydown', onKeydown, true);
    }
    function onDocClick(e) {
      if (!mountEl.contains(e.target)) close();
    }
    function onKeydown(e) {
      if (e.key === 'Escape') { close(); $trigger.focus(); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var items = [...$panel.querySelectorAll('[data-cl-select-option]:not([aria-disabled="true"])')];
        if (!items.length) return;
        var idx = items.indexOf(document.activeElement);
        var next = (e.key === 'ArrowDown') ? (idx + 1) % items.length : (idx - 1 + items.length) % items.length;
        items[next].focus();
      }
    }
    $trigger.addEventListener('click', function () { isOpen ? close() : open(); });

    function setOptions(list, value) {
      currentList = list || [];
      $panel.innerHTML = currentList.map(function (o) {
        var disabled = (o.status === 'not-offered');
        var muted = (o.status === 'oos');
        var classes = ['cl-css-select__option'];
        if (disabled) classes.push('is-disabled');
        if (muted) classes.push('is-muted');
        if (value === o.value) classes.push('is-selected');
        return '<li class="' + classes.join(' ') + '" role="option" data-cl-select-option ' +
               (disabled ? 'aria-disabled="true"' : 'tabindex="0"') +
               ' data-value="' + (o.value + '').replace(/"/g,'&quot;') + '">' +
                 '<span class="cl-css-select__opt-label">' + o.label + '</span>' +
                 (o.sub ? '<span class="cl-css-select__opt-sub">' + o.sub + '</span>' : '') +
               '</li>';
      }).join('');
      $panel.querySelectorAll('[data-cl-select-option]').forEach(function (li) {
        if (li.getAttribute('aria-disabled') === 'true') return;
        li.addEventListener('click', function () {
          var v = li.getAttribute('data-value');
          setValue(v);
          close();
          onChange(v);
        });
        li.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            li.click();
          }
        });
      });
      setValue(value);
    }
    function setValue(v) {
      currentValue = v;
      var match = currentList.find(function (o) { return o.value === v; });
      if (match) {
        var sub = match.sub ? ' — ' + match.sub : '';
        $value.textContent = match.label + sub;
        if (match.status === 'oos') $trigger.classList.add('is-oos'); else $trigger.classList.remove('is-oos');
      } else {
        $value.textContent = '';
      }
      $panel.querySelectorAll('[data-cl-select-option]').forEach(function (li) {
        if (li.getAttribute('data-value') === v) li.classList.add('is-selected');
        else li.classList.remove('is-selected');
      });
    }
    return { setOptions: setOptions, setValue: setValue, getValue: function () { return currentValue; }, close: close };
  }

  /* --- Public API ---------------------------------------------------------- */
  window.StixPicker = {
    catalog: catalog,
    oosRecs: oosRecs,
    findVariant: findVariantBySel,
    findVariantByOptionValues: findVariantByOptionValues,
    variantStatus: variantStatus,
    valueOffered: valueOffered,
    probeStatus: probeStatus,
    closestInStock: closestInStock,
    applyVariant: applyVariant,
    fireVariantEvents: fireVariantEvents,
    fireEvent: fireEvent,
    attachOosHero: attachOosHero,
    attachQuickPick: attachQuickPick,
    makeDropdown: makeDropdown,
    initialSel: initialSel,
    variantLabel: variantLabel
  };
})();
